#!/usr/bin/env node
/**
 * CLI for ludicrous-gateway
 * Command-line interface for managing the gateway
 */

import { Command } from "commander";
import { loadConfig, validateConfig } from "./config/index.js";
import { createServer, GatewayServer } from "./server/http-server.js";
import { initLogger, getLogger } from "./utils/logger.js";
import * as fs from "node:fs";
import * as path from "node:path";
import { fileURLToPath } from "node:url";
import yaml from "js-yaml";

// Get version from package.json dynamically
const __dirname = path.dirname(fileURLToPath(import.meta.url));
const packageJsonPath = path.resolve(__dirname, "../package.json");
const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, "utf-8"));

const program = new Command();

program
  .name("ludicrous-gateway")
  .description("Multi-agent LLM API Gateway for Claude Code")
  .version(packageJson.version);

/**
 * Serve command - start the gateway server
 */
program
  .command("serve")
  .description("Start the gateway server")
  .option("-c, --config <path>", "Path to configuration file")
  .option("-p, --port <port>", "Override listen port")
  .option("-h, --host <host>", "Override listen host")
  .option("-l, --log-level <level>", "Log level (debug, info, warn, error)")
  .action(async (options) => {
    try {
      // Load configuration
      const config = loadConfig({
        configPath: options.config,
        useDefaults: true,
      });

      // Apply CLI overrides
      if (options.port || options.host) {
        const [currentHost, currentPort] = config.server.listen.split(":");
        const host = options.host ?? currentHost ?? "127.0.0.1";
        const port = options.port ?? currentPort ?? "8787";
        config.server.listen = `${host}:${port}`;
      }

      if (options.logLevel) {
        config.logging.level = options.logLevel;
      }

      // Initialize logger
      initLogger(config.logging);
      const logger = getLogger();

      logger.info("Starting ludicrous-gateway", {
        listen: config.server.listen,
        providers: config.providers.filter((p) => p.enabled).map((p) => p.name),
        rules: config.routing.rules.length,
      });

      // Create and start server
      let server: GatewayServer;
      try {
        server = await createServer(config);
      } catch (error) {
        logger.error("Failed to start server", {
          error: error instanceof Error ? error.message : "Unknown error",
        });
        process.exit(1);
      }

      // Handle shutdown signals
      const shutdown = async (signal: string) => {
        logger.info(`Received ${signal}, shutting down...`);
        await server.stop();
        process.exit(0);
      };

      process.on("SIGINT", () => void shutdown("SIGINT"));
      process.on("SIGTERM", () => void shutdown("SIGTERM"));

      // Handle uncaught errors
      process.on("uncaughtException", (error) => {
        logger.error("Uncaught exception", { error: error.message });
        process.exit(1);
      });

      process.on("unhandledRejection", (reason) => {
        logger.error("Unhandled rejection", {
          reason: reason instanceof Error ? reason.message : String(reason),
        });
      });
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

/**
 * Status command - check gateway status
 */
program
  .command("status")
  .description("Check gateway status")
  .option("-u, --url <url>", "Gateway URL", "http://127.0.0.1:8787")
  .action(async (options) => {
    try {
      const healthUrl = `${options.url}/health`;
      const statsUrl = `${options.url}/stats`;

      console.log(`Checking gateway at ${options.url}...\n`);

      // Check health
      try {
        const healthRes = await fetch(healthUrl);
        const health = (await healthRes.json()) as {
          status: string;
          timestamp: string;
          providers: Record<string, string>;
        };

        console.log("Health Status:");
        console.log(`  Status: ${health.status}`);
        console.log(`  Timestamp: ${health.timestamp}`);
        console.log("  Providers:");
        for (const [name, status] of Object.entries(health.providers)) {
          console.log(`    - ${name}: ${status}`);
        }
      } catch {
        console.log("Health Status: UNREACHABLE");
        process.exit(1);
      }

      // Get stats
      try {
        const statsRes = await fetch(statsUrl);
        const stats = (await statsRes.json()) as {
          uptime_seconds: number;
          totalRequests: number;
          activeRequests: number;
        };

        console.log("\nStatistics:");
        console.log(`  Uptime: ${stats.uptime_seconds}s`);
        console.log(`  Total Requests: ${stats.totalRequests}`);
        console.log(`  Active Requests: ${stats.activeRequests}`);
      } catch {
        console.log("\nStatistics: Unable to fetch");
      }
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

/**
 * Config command - validate configuration
 */
program
  .command("config")
  .description("Validate configuration file")
  .argument("[path]", "Path to configuration file")
  .option("-p, --print", "Print parsed configuration")
  .action((path, options) => {
    try {
      // If path provided, load and validate that file
      if (path) {
        if (!fs.existsSync(path)) {
          console.error(`Error: Configuration file not found: ${path}`);
          process.exit(1);
        }

        const content = fs.readFileSync(path, "utf-8");
        const rawConfig = yaml.load(content);

        const result = validateConfig(rawConfig);

        if (!result.valid) {
          console.error("Configuration validation failed:");
          for (const error of result.errors) {
            console.error(`  - ${error}`);
          }
          process.exit(1);
        }

        console.log(`Configuration file is valid: ${path}`);

        if (options.print) {
          console.log("\nParsed configuration:");
          console.log(JSON.stringify(result.config, null, 2));
        }
      } else {
        // Try to find and validate default config
        try {
          const config = loadConfig({ useDefaults: true });
          console.log("Configuration is valid");

          if (options.print) {
            console.log("\nActive configuration:");
            console.log(JSON.stringify(config, null, 2));
          }
        } catch (error) {
          console.error(
            "Configuration error:",
            error instanceof Error ? error.message : "Unknown error"
          );
          process.exit(1);
        }
      }
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

/**
 * Route command - test routing for a request
 */
program
  .command("route")
  .description("Test routing for a sample request")
  .option("-c, --config <path>", "Path to configuration file")
  .option("-m, --model <model>", "Model name", "claude-opus-4-5-20251101")
  .option("-t, --tokens <count>", "Estimated token count", "1000")
  .option("-k, --keywords <keywords>", "Comma-separated keywords to include")
  .action((options) => {
    try {
      const config = loadConfig({
        configPath: options.config,
        useDefaults: true,
      });

      // Import router and analyzer dynamically
      import("./routing/router.js").then(({ createRouter }) => {
        import("./analyzers/content-analyzer.js").then(
          ({ DefaultContentAnalyzer }) => {
            const router = createRouter(config.routing);
            const analyzer = new DefaultContentAnalyzer({
              ruleKeywords: config.routing.rules.flatMap(
                (r) => r.conditions.keywords ?? []
              ),
              rulePatterns: config.routing.rules.flatMap(
                (r) => r.conditions.patterns ?? []
              ),
            });

            // Create mock request
            const keywords = options.keywords?.split(",") ?? [];
            const content = keywords.length > 0 ? keywords.join(" ") : "Hello";

            const mockRequest = {
              model: options.model,
              max_tokens: 4096,
              messages: [{ role: "user" as const, content }],
            };

            // Analyze and route
            const analysis = analyzer.analyze(mockRequest);
            analysis.estimatedTokens = parseInt(options.tokens, 10);

            if (keywords.length > 0) {
              analysis.matchedKeywords = keywords;
            }

            const decision = router.route(mockRequest, analysis);

            console.log("Routing Test Results:");
            console.log("=".repeat(40));
            console.log(`\nInput:`);
            console.log(`  Model: ${options.model}`);
            console.log(`  Tokens: ${options.tokens}`);
            console.log(`  Keywords: ${keywords.join(", ") || "(none)"}`);
            console.log(`\nAnalysis:`);
            console.log(`  Estimated Tokens: ${analysis.estimatedTokens}`);
            console.log(
              `  Matched Keywords: ${analysis.matchedKeywords.join(", ") || "(none)"}`
            );
            console.log(
              `  Sensitive Content: ${analysis.hasSensitiveContent}`
            );
            console.log(`\nRouting Decision:`);
            console.log(`  Provider: ${decision.provider}`);
            console.log(`  Model: ${decision.model}`);
            console.log(`  Rule: ${decision.matchedRule ?? "(default)"}`);
            console.log(`  Reason: ${decision.reason}`);
          }
        );
      });
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

/**
 * Setup command - interactive setup wizard
 */
program
  .command("setup")
  .description("Interactive setup wizard for configuring providers and routing")
  .action(async () => {
    try {
      const { runSetupWizard } = await import("./cli/setup-wizard.js");
      await runSetupWizard();
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

/**
 * Provider commands - manage providers
 */
const provider = program.command("provider").description("Manage LLM providers");

provider
  .command("add")
  .description("Add a new provider interactively")
  .action(async () => {
    try {
      const { addProvider } = await import("./cli/setup-wizard.js");
      await addProvider();
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

provider
  .command("test [name]")
  .description("Test provider connection")
  .action(async (name) => {
    try {
      const { testProvider } = await import("./cli/test-connection.js");
      await testProvider(name);
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

provider
  .command("list")
  .description("List configured providers")
  .action(async () => {
    try {
      const { listProviders } = await import("./cli/setup-wizard.js");
      await listProviders();
    } catch (error) {
      console.error(
        "Error:",
        error instanceof Error ? error.message : "Unknown error"
      );
      process.exit(1);
    }
  });

// Parse arguments
program.parse();
